import { useEffect, useState } from 'react';

/** Observe existing scroll owners, including the document; never register or move them. */
export function useCrmScrollTarget(enabled: boolean, routeKey: string) {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!enabled) { setTarget(null); return; }
    const workspace = document.querySelector('.crm-workspace');
    const documentTarget = document.scrollingElement as HTMLElement | null;
    const choose = (start: Element | null) => {
      for (let node = start; node && workspace?.contains(node); node = node.parentElement) {
        if (node instanceof HTMLElement && /auto|scroll/.test(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight) return node;
      }
      return documentTarget;
    };
    const refresh = () => setTarget(choose(workspace?.querySelector('main') ?? workspace));
    const intent = (event: Event) => {
      const node = event.target instanceof Element ? event.target : document.activeElement;
      if (node?.closest('.crm-hybrid-header,.crm-shared-sidebar,.crm-sidebar-dialog') || !workspace?.contains(node)) return;
      setTarget(choose(node));
    };
    refresh();
    // The document is only a fallback while the department mounts/loads. It
    // stays connected forever, so re-evaluate it when content appears rather
    // than waiting for the first user gesture (which would rebind listeners).
    // Keep an explicitly selected internal/list scroller across other updates.
    const observer = new MutationObserver(() => setTarget(current => current?.isConnected && current !== documentTarget
      ? current : choose(workspace?.querySelector('main') ?? workspace)));
    if (workspace) observer.observe(workspace, { childList: true, subtree: true });
    for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown']) document.addEventListener(event, intent, { capture: true, passive: true });
    return () => {
      observer.disconnect();
      for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown']) document.removeEventListener(event, intent, true);
    };
  }, [enabled, routeKey]);
  return target;
}
